/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { FC, useRef } from 'react'

import {
  Project,
  useCoordinator,
  useProject,
  useInfiniteProjects,
} from '@builddocs/data-management'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Box, CircularProgress, IconButton } from '@mui/material'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import {
  usePopupState,
  anchorRef,
  bindTrigger,
  bindMenu,
} from 'material-ui-popup-state/hooks'

type Props = {
  onChange: (p: string) => void
}

export const ProjectsSelector: FC<Props> = ({ onChange }) => {
  const { projectId } = useCoordinator()

  const { project } = useProject(projectId)

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'project-select',
  })

  const {
    projects,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteProjects({
    pageSize: 10,
    searchValue: '',
    enabled: !!popupState.isOpen,
  })

  const handleChange = (_e: any, value: Project | undefined) => {
    if (!value) return

    onChange(value.projectId)
    popupState.close()
  }

  const projectCardRef = useRef<HTMLUListElement>(null)

  const handleMenuScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget

    const distanceToBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight

    if (distanceToBottom < 100 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  return (
    <>
      <List ref={projectCardRef} disablePadding aria-label="Device settings">
        <ListItem disablePadding aria-label="PROJECT" id="select-project">
          <ListItem>
            <ListItemText
              primary={
                <>
                  <Box ref={anchorRef(popupState) as any} />
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <p>{project?.description || 'Не выбран'}</p>
                    <IconButton
                      aria-label="more"
                      edge="start"
                      sx={{
                        padding: 0,
                        borderRadius: 1,
                        position: 'absolute',
                        top: 16,
                        right: 0,
                      }}
                      {...bindTrigger(popupState)}
                    >
                      <ExpandMore sx={{ marginLeft: 0, marginBottom: 0 }} />
                    </IconButton>
                  </Box>
                </>
              }
              primaryTypographyProps={{
                sx: {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '3',
                  WebkitBoxOrient: 'vertical',
                  lineClamp: '3',
                  mb: 1,
                } as any,
              }}
              secondary={project?.constructionAddress || 'Проект'}
            />
          </ListItem>
        </ListItem>
      </List>
      <Menu
        {...bindMenu(popupState)}
        MenuListProps={{
          onScroll: handleMenuScroll,
          sx: {
            maxWidth: 512,
            maxHeight: 400,
            overflowY: 'auto',
          },
        }}
      >
        {isLoading && (
          <MenuItem disabled>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <CircularProgress size={20} />
            </Box>
          </MenuItem>
        )}
        {!isLoading &&
          projects.map(option => (
            <MenuItem
              key={option.projectId}
              selected={option.projectId === projectId}
              onClick={event => handleChange(event, option)}
            >
              <ListItemText
                primary={option.description}
                secondary={option.constructionAddress}
                sx={{ whiteSpace: 'normal', overflowWrap: 'break-word' }}
              />
            </MenuItem>
          ))}
        {isFetchingNextPage && (
          <MenuItem disabled>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <CircularProgress size={20} />
            </Box>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}
